import { SelectionModel } from "@angular/cdk/collections";
import { HttpClient } from "@angular/common/http";
import { Component, ElementRef, ViewChild } from "@angular/core";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { Router } from "@angular/router";
import { StorageService } from "src/services/storage.service";
import { NewCustomer } from "src/models/newcustomer";
import { TableComponent } from "../table.component";
import { RenewComprehensiveCustomerService } from "src/services/renew-comprehensive-customer.service";
import { Helpers } from "src/helpers/helpers";
import jwt_decode from "jwt-decode";
import { AgencyService } from "../agency/services/agency.service";
import { Batch } from "src/models/agency";
import * as Papa from "papaparse";
import { of } from "rxjs";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { AlertDialogComponent } from "../alert-dialog/alert-dialog.component";
import { DeleteData } from "../agency/services/delete-data";
import { AccountService } from "src/services/account.service";

@Component({
  selector: "app-renew-comprehensive-customers",
  templateUrl: "./renew-comprehensive-customers.component.html",
  styleUrls: ["./renew-comprehensive-customers.component.css"],
})
export class RenewComprehensiveCustomersComponent extends TableComponent<NewCustomer> {
  newCustomersFile: any;
  newCustomersEle: string;
  startExpiryDate: Date;
  endExpiryDate: Date;

  processing = false;
  isPanelOpen = false;
  canDownloadData = false;
  cloudEnabled = false;
  @ViewChild("csvFileInput") csvFileInput: ElementRef;

  displayedColumns: string[] = [
    "select",
    "id",
    "importedAt",
    "status",
    "ownerName",
    "countryCode",
    "phoneNumber1",
    "phoneNumber2",
    "phoneNumber3",
    "phoneNumber4",
    "plateNumber",
    "carMake",
    "carModel",
    "modelYear",
    "nationality",
    "chassisNumber",
    "color",
    "expiryDate",
    "gender",
    "dateOfBirth",
    "email",
    "refferByName",
    "referrerId",
    "vehicleValue",
    "source",
  ];
  selection = new SelectionModel<any>(true, []);
  isAgency = false;
  isCreatingBatch = false;
  source = undefined;
  cloudBatches: Batch[] = [];
  private _agencyFullData: NewCustomer[] = [];

  constructor(
    private storageService: StorageService,
    private _agencyService: AgencyService,
    private snackBar: MatSnackBar,
    private router: Router,
    protected http: HttpClient,
    protected renewComprehensiveCustomerService: RenewComprehensiveCustomerService,
    private helper: Helpers,
    private dialog: MatDialog,
    private accountService: AccountService
  ) {
    super(renewComprehensiveCustomerService);
    const obj = this.getDecodedAccessToken(this.helper.getToken());
    const userType = obj.type;
    if (
      userType.indexOf("LEVEL 0") === 0 ||
      userType.indexOf("LEVEL 1") === 0 ||
      userType.indexOf("LEVEL 2") === 0
    ) {
      this.isAgency = true;
      this._agencyService.getAgency().subscribe((agency) => {
        this.helper.setAgencyId(agency.id);
        this.source = agency.name;
      });
    }
    if (
      userType.indexOf("LEVEL 1") === 0 ||
      userType.indexOf("LEVEL 2") === 0
    ) {
      this.canDownloadData = true;
    }
    this.dataSubject.next([]);
    this.dataSubject.subscribe((data) => {
      this.isPanelOpen = data?.length === 0;
    });
  }

  ngOnInit(): void {
    if (!this.isAgency) {
      super.ngOnInit();
      return;
    }

    this.accountService.getAccount().subscribe({
      next: (account) => {
        this.cloudEnabled = account.agency.cloudEnabled;

        if (this.cloudEnabled) {
          this.loadCloudBatches();
        } else {
          this._agencyFullData = this.helper.getImportedData() ?? [];
          this.count = this._agencyFullData.length;
          this.loadRequests();
        }
      },
      error: () => {
        this.cloudEnabled = false;
        this._agencyFullData = this.helper.getImportedData() ?? [];
        this.count = this._agencyFullData.length;
        this.loadRequests();
      },
    });
  }

  private loadCloudBatches(): void {
    this._agencyService.getAgency().subscribe({
      next: (agency) => {
        const customers: NewCustomer[] = [];

        agency.batches?.forEach((batch) => {
          batch.batchData?.forEach((customer) => {
            const newCustomer = customer as unknown as NewCustomer;
            newCustomer.batchId = batch.id;
            newCustomer.importedAt = batch.importedAt.toString();
            newCustomer.whatsAppStatus = batch.whatsAppStatus;
            newCustomer.emailStatus = batch.emailStatus;
            customers.push(newCustomer);
          });
        });

        this._agencyFullData = customers;
        this.count = customers.length;
        this.loadRequests();
      },
      error: () => {
        this._agencyFullData = [];
        this.count = 0;
        this.loadRequests();
      },
    });
  }

  getDecodedAccessToken(token: string): any {
    try {
      return jwt_decode(token);
    } catch (Error) {
      return null;
    }
  }
  uploadCSV() {
    if (this.newCustomersFile !== undefined) {
      if (this.isAgency) {
        this.processCSVLocally();
      } else {
        const d = new Date();
        this.storageService
          .getUploadOnlyTokenForContainer("renew-comprehensivecustomer-csv")
          .subscribe((token) => {
            this.storageService
              .uploadFileToAzure(
                `/renew-comprehensivecustomer-csv/${
                  d.getTime().toString() + ".csv"
                }${token.sasToken}`,
                this.newCustomersFile,
                "text/csv"
              )
              .subscribe({
                next: () => {
                  this.snackBar.open(
                    "The file has been uploaded. An email will be sent to your account as soon as processed.",
                    "CANCEL",
                    {
                      horizontalPosition: "right",
                      duration: 4000,
                    }
                  );
                  location.reload();
                },
              });
          });
      }
    }
  }

  private processCSVLocally() {
    this.isCreatingBatch = true;
    Papa.parse(this.newCustomersFile, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      transformHeader: (header: string) => {
        return header.charAt(0).toLowerCase() + header.slice(1);
      },
      complete: (result: { data: NewCustomer[] }) => {
        const batch: Batch = {
          id: 0,
          importedAt: new Date(),
          whatsAppStatus: "PENDING",
          emailStatus: "PENDING",
          agencyId: "",
          agencyUserId: "",
          failedPolicyCount: 0,
          importedPolicyCount: result.data.length,
          createdBy: "",
        };

        const retrievedData = this.helper.getImportedData();
        let newData: NewCustomer[] = [];
        const failedData: NewCustomer[] = [];
        if (retrievedData) {
          // Retrieve another entry with same plate number
          for (const data of result.data) {
            data.source = this.source ?? data.source;
            const existingData = retrievedData.find(
              (d: NewCustomer) =>
                data.plateNumber?.toString() === d.plateNumber?.toString()
            );
            if (!existingData) {
              const error = this.getError(data);
              if (error) {
                failedData.push({
                  error: error,
                  ...data,
                });
                continue;
              }

              newData.push({
                ...data,
                phoneNumber1: data.phoneNumber1?.toString() ?? "",
                phoneNumber2: data.phoneNumber2?.toString() ?? "",
                phoneNumber3: data.phoneNumber3?.toString() ?? "",
                phoneNumber4: data.phoneNumber4?.toString() ?? "",
                plateNumber: data.plateNumber?.toString() ?? "",
                vehicleValue: data.vehicleValue ?? 0,
                expiryDate: this.parseDateString(data.expiryDate),
                batchId: batch.id,
                createdAt: new Date(),
              });
            } else {
              failedData.push({
                ...data,
                error: "Duplicate plate number",
              });
            }
          }
        } else {
          for (const data of result.data) {
            data.source = this.source ?? data.source;
            const error = this.getError(data);
            if (error) {
              failedData.push({
                error: error,
                ...data,
              });
              continue;
            }
            newData.push({
              ...data,
              phoneNumber1: data.phoneNumber1?.toString() ?? "",
              phoneNumber2: data.phoneNumber2?.toString() ?? "",
              phoneNumber3: data.phoneNumber3?.toString() ?? "",
              phoneNumber4: data.phoneNumber4?.toString() ?? "",
              plateNumber: data.plateNumber?.toString() ?? "",
              vehicleValue: data.vehicleValue ?? 0,
              expiryDate: this.parseDateString(data.expiryDate),
              batchId: batch.id,
              createdAt: new Date(),
            });
          }
        }
        if (newData.length === 0 && failedData.length === 0) {
          this.isCreatingBatch = false;
          this.snackBar.open(
            "No records were imported. Please check the file and try again.",
            "CANCEL",
            {
              horizontalPosition: "right",
              duration: 20000,
            }
          );
        } else if (newData.length === 0 && failedData.length > 0) {
          this.isCreatingBatch = false;
          this.newCustomersEle = "";
          this.newCustomersFile = undefined;
          this.csvFileInput.nativeElement.value = null;
          const text = `Your file has ${result.data.length} rows of data. ${newData.length} rows have been imported successfully. ${failedData.length} rows of data have been skipped due to errors. A new file with the skipped data can be downloaded by click 'Downlod Report'. Call us in case you need any assistance.`;
          this.snackBar
            .open(text, "DOWNLOAD REPORT", {
              horizontalPosition: "right",
              duration: 60000,
            })
            .onAction()
            .subscribe(() => {
              this.downloadFailedData(failedData);
            });
        } else {
            batch.failedPolicyCount =
            this.isAgency && this.cloudEnabled ? 0 : failedData.length;
          batch.importedPolicyCount =
            this.isAgency && this.cloudEnabled ? 0 : newData.length;
          this._agencyService.createBatch(batch).subscribe({
            next: (batch) => {
              if (this.isAgency && this.cloudEnabled) {
                this.uploadToCloud(this.newCustomersFile, batch.id);
              } else {
                const newCustomersWithBatchId = newData.map((data) => ({
                  ...data,
                  batchId: batch.id,
                }));
                this._agencyFullData = [...this._agencyFullData, ...newCustomersWithBatchId];
                this.count = this._agencyFullData.length;
                this.helper.appendImportedData(newCustomersWithBatchId);
                this.loadRequests();
              }

              this.isCreatingBatch = false;
              this.newCustomersEle = "";
              this.newCustomersFile = undefined;
              this.csvFileInput.nativeElement.value = null;
              const text = `Your file has ${result.data.length} rows of data. ${newData.length} rows have been imported successfully. ${failedData.length} rows of data have been skipped due to errors. A new file with the skipped data can be downloaded by click 'Download Report'. Call us in case you need any assistance.`;
              this.snackBar
                .open(text, "DOWNLOAD REPORT", {
                  horizontalPosition: "right",
                  duration: 60000,
                })
                .onAction()
                .subscribe(() => {
                  this.downloadFailedData(failedData);
                });
            },
            error: (err) => {
              this.snackBar.open(err, "CANCEL", {
                horizontalPosition: "right",
                duration: 4000,
              });
            },
          });
        }
      },
    });
  }

  private uploadToCloud(file: File, batchId: number) {
    const d = new Date().getTime().toString();
    const renamedFileName = `${d}-batch:${batchId}`;
    this.storageService
      .getUploadOnlyTokenForContainer("renew-comprehensivecustomer-csv")
      .subscribe((token) => {
        this.storageService
          .uploadFileToAzure(
            `/renew-comprehensivecustomer-csv/${renamedFileName}.csv${token.sasToken}`,
            file,
            "text/csv"
          )
          .subscribe({
            next: () => {

              setTimeout(() => {
                this.loadCloudBatches();
              }, 2000);
            },
            error: (err) => {
              this.snackBar.open("Failed to upload data to cloud.", "CLOSE", {
                duration: 5000,
                horizontalPosition: "right",
              });
            },
          });
      });
  }
  private parseDateString(dateStr: string | undefined): string {
    if (!dateStr) return "";

    const dateString = dateStr.toString().trim();
    if (!dateString) return "";

    const parts = dateString.split(/[-\/]/);
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      const date = new Date(Date.UTC(year, month, day));

      return date.toISOString();
    }

    return dateString;
  }

  getError(data: NewCustomer): string | null {
    let error = "";
    if (!data.plateNumber) {
      error += "Invalid plate number, ";
    }
    if (!data.phoneNumber1) {
      error += "Invalid phone number, ";
    }
    if (!data.ownerName) {
      error += "Invalid owner name, ";
    }
    if (!data.countryCode) {
      error += "Invalid country code, ";
    }
    if (!data.email) {
      error += "Invalid email, ";
    }
    if (!data.carMake) {
      error += "Invalid car make, ";
    }
    if (!data.carModel) {
      error += "Invalid car model, ";
    }
    if (!data.modelYear) {
      error += "Invalid model year, ";
    }
    if (!data.vehicleValue) {
      error += "Invalid vehicle value, ";
    }

    return error.length > 0 ? error : null;
  }

  downloadFailedData(failedData: NewCustomer[]) {
    const failedDataCsv = Papa.unparse(
      failedData.map((data) => ({
        error: data.error ? data.error : "",
        ...data,
      }))
    );
    const blob = new Blob([failedDataCsv], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "failed-data.csv";
    a.click();
  }

  downloadImportData() {
    const data = this.isAgency && this.cloudEnabled? (this._agencyFullData ?? []) : (this.helper.getImportedData() ?? []);
    const importedDataCsv = Papa.unparse(data);
    const blob = new Blob([importedDataCsv], {
      type: "text/csv",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "imported-data.csv";
    a.click();
  }

  onnewCustomers(event) {
    const file = event.target.files[0];
    this.newCustomersEle = file.name;
    this.newCustomersFile = file;
  }

  openCustomer(row) {
    if (!this.isAgency) {
      this.router.navigate(["/renew-comprehensive-customers/" + row.id]);
    }
  }

  hasPurchased(purchased) {
    return purchased ? "Yes" : "No";
  }

  getTableData() {
    if (this.isAgency) {
      let data = [...this._agencyFullData];
      const filter = this.input?.nativeElement?.value?.toLowerCase();

      if (filter) {
        data = data.filter((item) =>
          Object.values(item).some((v) =>
            v?.toString().toLowerCase().includes(filter)
          )
        );
      }

      const pageSize = this.paginator?.pageSize ?? 10;
      const pageIndex = this.paginator?.pageIndex ?? 0;
      const start = pageIndex * pageSize;

      return of(data.slice(start, start + pageSize));
    }
    return super.getTableData();
  }

  getCount() {
    if (this.isAgency) {
      const data = this._agencyFullData;
      const filter = this.input?.nativeElement?.value?.toLowerCase();

      const count = filter
        ? data.filter((item) =>
            Object.values(item).some((v) =>
              v?.toString().toLowerCase().includes(filter)
            )
          ).length
        : data.length;

      return of({ count });
    }

    return this.renewComprehensiveCustomerService.getCount({
      filter: this.input?.nativeElement?.value ?? "",
      startExpiryDate: this.startExpiryDate,
      endExpiryDate: this.endExpiryDate,
    });
  }

  protected getData(): any {
    let pageSize = this.paginator?.pageSize ? this.paginator.pageSize : 10;
    return {
      skip: (this.paginator?.pageIndex ?? 0) * pageSize,
      take: pageSize,
      filter: this.input?.nativeElement?.value ?? "",
      orderBy: this.sort?.active,
      orderByDescending: this.sort?.direction === "desc",
      startExpiryDate: this.startExpiryDate,
      endExpiryDate: this.endExpiryDate,
    };
  }

  filter() {
    this.paginator.pageIndex = 0;
    this.getCount().subscribe((res) => {
      this.count = res.count;
      this.loadRequests();
    });
  }

  sendComprehensiveLink() {
    const formattedReqPayload = this.selection?.selected
      .map((selectedRow) => {
        const detectedLang = this.detectLanguage(selectedRow?.ownerName);
        return { id: selectedRow?.id, lang: detectedLang };
      })
      .filter((item) => item !== null);

    if (formattedReqPayload?.length === 0) {
      console.error("No valid payload generated");
      return;
    }

    this.renewComprehensiveCustomerService
      .sendComprehensiveLink(formattedReqPayload)
      .subscribe(
        (res) => {
          this.snackBar.open(
            "The policy renewal reminder is sent successfully.",
            "CANCEL",
            {
              horizontalPosition: "right",
              duration: 4000,
            }
          );
        },
        (err) => {
          this.snackBar.open(err, "CANCEL", {
            horizontalPosition: "right",
            duration: 4000,
          });
        }
      );
  }

  detectLanguage(ownerName: string): string {
    if (!ownerName) return "unknown";

    const arabicRegex = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
    if (arabicRegex.test(ownerName)) {
      return "ar";
    }
    const englishRegex = /^[A-Za-z0-9\s]+$/;
    if (englishRegex.test(ownerName)) {
      return "en";
    }
    return "unknown";
  }
  downloadSampleCSV() {
    const link = document.createElement("a");
    link.href = "assets/files/template.csv";
    link.download = "template.csv";
    link.click();
  }

  deleteSelectedData() {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: {
        title: "Delete Selected Data",
        message: "Are you sure you want to delete the selected data?",
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === "true") {
        const selectedData = this.selection.selected;

        if (this.cloudEnabled) {
          const cloudData = this._agencyFullData;
          const batchMap = new Map<number, number[]>();

          for (const selectedItem of selectedData) {
            const cloudEntry = cloudData.find(
              (item) =>
                item.plateNumber === selectedItem.plateNumber &&
                item.batchId === selectedItem.batchId
            );

            if (cloudEntry && cloudEntry.id) {
              const ids = batchMap.get(selectedItem.batchId) ?? [];
              ids.push(cloudEntry.id);
              batchMap.set(selectedItem.batchId, ids);
            }
          }

          const deleteData: DeleteData[] = [];
          for (const [batchId, ids] of batchMap.entries()) {
            deleteData.push({
              batchId: batchId,
              count: ids.length,
              ids: ids,
            });
          }

          if (deleteData.length > 0) {
            this._agencyService.deleteData(deleteData).subscribe({
              next: () => {
                this.snackBar.open(
                  "The selected data has been deleted successfully.",
                  "CANCEL",
                  {
                    horizontalPosition: "right",
                    duration: 4000,
                  }
                );
                this.selection.clear();
                this.loadCloudBatches();
              },
              error: (err) => {
                this.snackBar.open(err, "CANCEL", {
                  horizontalPosition: "right",
                  duration: 4000,
                });
              },
            });
          } else {
            this.snackBar.open("No data selected for deletion.", "CANCEL", {
              horizontalPosition: "right",
            });
          }
        } else {
          const importedData = this.helper.getImportedData();

          // Get all data that can be deleted
          const canDeleteData = importedData.filter(
            (item) => item.canDelete !== false
          );
          if (canDeleteData.length === 0) {
            this.snackBar.open(
              "The selected data can not be deleted because it is already processed.",
              "CANCEL",
              {
                horizontalPosition: "right",
              }
            );
            return;
          }
          // Create a map of batchId and an array of data to delete for each batch
          const canDeleteDataMap = new Map<number, NewCustomer[]>();
          for (const item of canDeleteData) {
            // If the data is selected to delete, add it to the map
            if (selectedData.some((i) => i.plateNumber === item.plateNumber)) {
              canDeleteDataMap.set(item.batchId, [
                ...(canDeleteDataMap.get(item.batchId) ?? []),
                item,
              ]);
            }
          }

          const deleteData: DeleteData[] = [];
          // Create an array of data to delete for each batch
          for (const item of selectedData) {
            const data = canDeleteDataMap.get(item.batchId);
            if (data && data.length > 0) {
              deleteData.push({
                batchId: item.batchId,
                count: data.length,
              });
            }
          }

          // If there is data to delete, delete it
          if (deleteData.length > 0) {
            this._agencyService.deleteData(deleteData).subscribe({
              next: () => {
                const dataNotToDelete = importedData.filter(
                  (item) =>
                    !canDeleteDataMap
                      .get(item.batchId)
                      ?.some((i) => i.plateNumber === item.plateNumber)
                );
                this.helper.replaceImportedData(dataNotToDelete);
                this._agencyFullData = dataNotToDelete;
                this.count = dataNotToDelete.length;
                this.selection.clear();
                let message = "";
                if (dataNotToDelete.length === importedData.length) {
                  message =
                    "The selected data can not be deleted because it is already processed.";
                } else if (
                  importedData.length - dataNotToDelete.length !==
                  selectedData.length
                ) {
                  message =
                    "Some data was not deleted because it was already processed. ";
                } else {
                  message = "The selected data has been deleted successfully.";
                }
                this.snackBar.open(message, "CANCEL", {
                  horizontalPosition: "right",
                  duration: 4000,
                });
                this.paginator.pageIndex = 0;
                this.loadRequests();
              },
              error: (err) => {
                this.snackBar.open(err, "CANCEL", {
                  horizontalPosition: "right",
                  duration: 4000,
                });
              },
            });
          } else {
            this.snackBar.open(
              "The selected data can not be deleted because it is already processed.",
              "CANCEL",
              {
                horizontalPosition: "right",
              }
            );
          }
        }
      }
    });
  }
}
